import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import type { CameraCalibrationMessage, PackedLayoutMessage, PoseFrameMessage } from '../protocol.ts';
import { toWireLandmarks } from './wireLandmarks.ts';

const WASM_ROOT = '/wasm';
const POSE_MODEL = '/models/pose_landmarker_lite.task';

export interface PackedColourCrop {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

export function packedColourCrop(
  videoWidth: number,
  videoHeight: number,
  layout?: Pick<PackedLayoutMessage, 'colour'>,
): PackedColourCrop {
  if (!Number.isFinite(videoWidth) || !Number.isFinite(videoHeight) || videoWidth <= 1 || videoHeight <= 0) {
    throw new Error('Packed video dimensions are unavailable');
  }
  const rect = layout?.colour ?? { x: 0, y: 0, width: 0.5, height: 1 };
  return {
    sx: rect.x * videoWidth,
    sy: rect.y * videoHeight,
    sw: rect.width * videoWidth,
    sh: rect.height * videoHeight,
  };
}

export interface FloorPoseDeriverOptions {
  video: HTMLVideoElement;
  calibration: () => CameraCalibrationMessage | undefined;
  packedLayout: () => PackedLayoutMessage | undefined;
  onPose: (frame: PoseFrameMessage) => void;
  onInference: (milliseconds: number) => void;
  onError?: (reason: string) => void;
}

export class FloorPoseDeriver {
  readonly #video: HTMLVideoElement;
  readonly #calibration: () => CameraCalibrationMessage | undefined;
  readonly #packedLayout: () => PackedLayoutMessage | undefined;
  readonly #onPose: (frame: PoseFrameMessage) => void;
  readonly #onInference: (milliseconds: number) => void;
  readonly #onError?: (reason: string) => void;
  readonly #canvas = document.createElement('canvas');
  readonly #context: CanvasRenderingContext2D;
  #landmarker?: PoseLandmarker;
  #generation = 0;
  #frameCallback = 0;
  #animationFrame = 0;
  #sequence = 0;
  #running = false;

  constructor(options: FloorPoseDeriverOptions) {
    this.#video = options.video;
    this.#calibration = options.calibration;
    this.#packedLayout = options.packedLayout;
    this.#onPose = options.onPose;
    this.#onInference = options.onInference;
    this.#onError = options.onError;
    const context = this.#canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('FLOOR pose inference canvas is unavailable');
    this.#context = context;
  }

  async start(): Promise<void> {
    this.stop();
    this.#running = true;
    const generation = ++this.#generation;
    try {
      const files = await FilesetResolver.forVisionTasks(WASM_ROOT);
      const landmarker = await PoseLandmarker.createFromOptions(files, {
        baseOptions: { modelAssetPath: POSE_MODEL },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.55,
        minPosePresenceConfidence: 0.55,
        minTrackingConfidence: 0.55,
        outputSegmentationMasks: false,
      });
      if (!this.#running || generation !== this.#generation) {
        landmarker.close();
        return;
      }
      this.#landmarker = landmarker;
      this.#schedule();
    } catch (error) {
      if (generation === this.#generation) {
        this.#running = false;
        this.#onError?.(error instanceof Error ? error.message : 'FLOOR pose model failed');
      }
    }
  }

  #schedule(): void {
    if (!this.#running || !this.#landmarker) return;
    if ('requestVideoFrameCallback' in this.#video) {
      this.#frameCallback = this.#video.requestVideoFrameCallback((now, metadata) => {
        this.#frameCallback = 0;
        try {
          this.#process(now, metadata.captureTime);
        } catch (error) {
          this.#fail(error);
        } finally {
          this.#schedule();
        }
      });
    } else {
      this.#animationFrame = requestAnimationFrame((now) => {
        this.#animationFrame = 0;
        try {
          this.#process(now);
        } catch (error) {
          this.#fail(error);
        } finally {
          this.#schedule();
        }
      });
    }
  }

  #fail(error: unknown): void {
    this.stop();
    this.#onError?.(error instanceof Error ? error.message : 'FLOOR pose inference failed');
  }

  #process(now: number, captureTime?: number): void {
    const landmarker = this.#landmarker;
    const calibration = this.#calibration();
    if (!this.#running || !landmarker || !calibration || this.#video.readyState < 2) return;
    const crop = packedColourCrop(this.#video.videoWidth, this.#video.videoHeight, this.#packedLayout());
    const outputWidth = Math.min(640, Math.max(2, Math.round(calibration.image.widthPx)));
    const outputHeight = Math.max(2, Math.round(outputWidth * calibration.image.heightPx / calibration.image.widthPx));
    if (this.#canvas.width !== outputWidth || this.#canvas.height !== outputHeight) {
      this.#canvas.width = outputWidth;
      this.#canvas.height = outputHeight;
    }
    const started = performance.now();
    this.#context.drawImage(
      this.#video,
      crop.sx, crop.sy, crop.sw, crop.sh,
      0, 0, outputWidth, outputHeight,
    );
    const result = landmarker.detectForVideo(this.#canvas, now);
    try {
      this.#onInference(performance.now() - started);
      const source = result.landmarks[0];
      const candidateCapture = typeof captureTime === 'number' && Number.isFinite(captureTime)
        ? (captureTime > 1_000_000_000_000 ? captureTime - performance.timeOrigin : captureTime)
        : now;
      const candidateAge = now - candidateCapture;
      const localCapture = candidateAge >= 0 && candidateAge <= 10_000 ? candidateCapture : now;
      const landmarks = source ? toWireLandmarks(source) : undefined;
      this.#onPose({
        kind: 'pose-frame',
        sequence: this.#sequence++,
        status: landmarks ? 'live' : 'paused',
        reason: landmarks ? undefined : 'Laptop pose is uncertain: paused',
        capturePerformanceMs: localCapture,
        captureEpochMs: performance.timeOrigin + localCapture,
        clockOffsetMs: 0,
        clockSynchronized: false,
        frameWidthPx: calibration.image.widthPx,
        frameHeightPx: calibration.image.heightPx,
        landmarks,
      });
    } finally {
      result.close();
    }
  }

  stop(): void {
    this.#running = false;
    this.#generation += 1;
    if (this.#frameCallback && 'cancelVideoFrameCallback' in this.#video) {
      this.#video.cancelVideoFrameCallback(this.#frameCallback);
    }
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
    this.#frameCallback = 0;
    this.#animationFrame = 0;
    this.#landmarker?.close();
    this.#landmarker = undefined;
    this.#context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
    this.#sequence = 0;
  }
}
